import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js'
import { useEffect, useState } from 'react'
import { Bar } from 'react-chartjs-2'

import { PortfolioStatsCategory } from 'enums/PortfolioStatsCategoryEnum'
import { AllocationFilter, Breakdown, BreakdownsStatistics } from 'types'

import PDSBox from 'designSystem/components/PDSBox/PDSBox'
import PDSControlGroup, {
  PDSControlGroupOption,
} from 'designSystem/components/PDSControlGroup/PDSControlGroup'
import PDSFlexBox from 'designSystem/components/PDSFlexBox/PDSFlexBox'
import PDSText from 'designSystem/components/PDSText/PDSText'
import { COLOR } from 'designSystem/foundations/constants/color'
import { SIZE } from 'designSystem/foundations/constants/size'
import { genPortfolioBreakdownChartData } from 'utils/breakdownUtils'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
)

type Props = {
  breakdowns: BreakdownsStatistics[]
  benchmarkName: string
}

const BreakdownOptions = [
  { label: 'Sector', value: AllocationFilter.Sector },
  { label: 'Geography', value: AllocationFilter.Geography },
  { label: 'Asset Class', value: AllocationFilter.AssetClass },
]

const options = {
  barThickness: 16,
  borderColor: 'white',
  borderWidth: {
    left: 2,
    right: 2,
  },
  hoverBorderColor: 'white',
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top' as const,
      display: false,
    },
    tooltip: {
      callbacks: {
        label: (bar: any) => {
          return bar.dataset.label + ' ' + bar.formattedValue + '%'
        },
      },
    },
  },
  responsive: true,
  scales: {
    y: {
      ticks: {
        callback: (value: number | string) => value + '%',
      },
    },
  },
}

export default function PortfolioBreakdowns({
  breakdowns,
  benchmarkName,
}: Props) {
  const [filter, setFilter] = useState<AllocationFilter>(
    AllocationFilter.Sector
  )
  const [datasets, setDatasets] = useState<any>([])
  const [labels, setLabels] = useState<any>([])
  const [hasOptimizedData, setHasOptimizedData] = useState<boolean>(false)
  const [hasBenchmarkData, setHasBenchmarkData] = useState<boolean>(false)

  const chartData = {
    labels,
    datasets,
  }

  useEffect(() => {
    const currentPortfolioData = breakdowns?.find(
      (b) => b.key === PortfolioStatsCategory.CURRENT
    )?.breakdowns
    const optimizedPortfolioData = breakdowns?.find(
      (b) => b.key === PortfolioStatsCategory.OPTIMIZED
    )?.breakdowns
    const benchmarkData = breakdowns?.find(
      (b) => b.key === PortfolioStatsCategory.BENCHMARK
    )

    const currentBreakdown = currentPortfolioData?.find((d: Breakdown) => {
      return d.name === filter
    })
    const optimizedBreakdown = optimizedPortfolioData?.find((d: Breakdown) => {
      return d.name === filter
    })
    const benchmarkBreakdown = benchmarkData?.breakdowns?.find(
      (d: Breakdown) => {
        return d.name === filter
      }
    )

    if (optimizedBreakdown) {
      setHasOptimizedData(true)
    }
    if (benchmarkBreakdown) {
      setHasBenchmarkData(true)
    }

    const { datasets, labels } = genPortfolioBreakdownChartData(
      currentBreakdown,
      optimizedBreakdown,
      benchmarkBreakdown
    )

    setDatasets(datasets)
    setLabels(labels)
    // set the other values
  }, [breakdowns, filter])

  return (
    <PDSFlexBox
      flexDirection="column"
      marginTop={SIZE.s16}
      width="100%"
      height="100%"
    >
      <PDSFlexBox width="100%" justifyContent="space-between">
        <PDSFlexBox width="100%" alignItems="baseline" gap={SIZE.s8}>
          <PDSFlexBox alignItems="center" gap={SIZE.s8}>
            <PDSBox
              backgroundColor={COLOR.grey500}
              borderRadius="50%"
              flexShrink={0}
              height="12px"
              width="12px"
            />
            <PDSText color={COLOR.textSecondary} size="sm" weight="medium">
              Current Portfolio
            </PDSText>
          </PDSFlexBox>
          {hasOptimizedData && (
            <PDSFlexBox alignItems="center" gap={SIZE.s8}>
              <PDSBox
                backgroundColor={COLOR.purple400}
                borderRadius="50%"
                flexShrink={0}
                height="12px"
                width="12px"
              />
              <PDSText color={COLOR.textSecondary} size="sm" weight="medium">
                Optimized Portfolio
              </PDSText>
            </PDSFlexBox>
          )}
          {hasBenchmarkData && (
            <PDSBox alignItems="center" gap={SIZE.s8}>
              <PDSFlexBox alignItems="center" gap={SIZE.s8}>
                <PDSBox
                  backgroundColor={COLOR.blue400}
                  borderRadius="50%"
                  flexShrink={0}
                  height="12px"
                  width="12px"
                />
                <PDSText color={COLOR.textSecondary} size="sm" weight="medium">
                  Benchmark
                </PDSText>
              </PDSFlexBox>
              <PDSText
                color={COLOR.textTertiary}
                size="sm"
                weight="medium"
                paddingLeft={SIZE.s20}
              >
                {benchmarkName}
              </PDSText>
            </PDSBox>
          )}
        </PDSFlexBox>

        <PDSFlexBox gap={SIZE.s4} alignItems="center">
          <PDSControlGroup
            defaultChecked
            defaultValue={AllocationFilter.Sector}
            label="View by"
            mainOnClick={(o: PDSControlGroupOption<AllocationFilter>) => {
              setFilter(o.value)
            }}
            size="sm"
            options={BreakdownOptions}
          />
        </PDSFlexBox>
      </PDSFlexBox>
      <PDSFlexBox height="16rem" marginTop={SIZE.s16} width="100%">
        <Bar options={options} data={chartData} />
      </PDSFlexBox>
    </PDSFlexBox>
  )
}
